/**
 * Sites host: the domain-specific Firestore writes injected into the shared
 * CreateFlow. A site is just a url doc, so these never touch a userTesting
 * config or a pluginUserTesting bridge.
 *
 * Draft lifecycle: createDraft writes a not-live draft url doc the moment the
 * first source is picked; finish* clears the draft flag as the real item goes
 * live; watchLive flips an agent-published draft live the instant its deploy
 * lands; discard deletes it (shared helper).
 */
import firebase from '../../firebase';
import { generateReadableSlug, generateUniqueSlug, isValidReadableSlug } from './sitesUtils';
import { editFigmaUrl } from '../../utils/figmaEmbedUrl';
import { getDefaultViewports, viewportsToLegacyFormat } from '../../utils/viewportUtils';
import { discardDraft } from '../../utils/drafts';
import { recordCreationSource } from '../../utils/creationSource';
import { startTrialClock } from '../../utils/trialClock';

export function buildSiteCreateActions(uid) {
  const db = firebase.firestore();
  const del = firebase.firestore.FieldValue.delete();
  const urlRef = (id) => db.collection('user').doc(uid).collection('url').doc(id);

  return {
    // A site draft is a plain html url doc flagged draft:true with a readable
    // slug as its id (the same shape New Site used, minus going live). The
    // title carries the typed name so the agent's name-matched deploy lands
    // on exactly this doc.
    // An optional caller-supplied slug lets the agent flow pre-mint the id and
    // share it between the draft and any agent-created site (so both converge
    // on one id). When absent, behaviour is exactly as before: mint a fresh
    // readable slug. An invalid supplied slug is ignored in favour of a minted
    // one rather than writing a doc at a bad id.
    async createDraft({ source, name, slug: suppliedSlug }) {
      const slug = (suppliedSlug && isValidReadableSlug(suppliedSlug))
        ? suppliedSlug
        : await generateReadableSlug(db);
      await urlRef(slug).set({
        userId: uid,
        type: 'html',
        brand: 'aero',
        generatedUrl: slug,
        title: (name || '').trim() || slug,
        draft: true,
        draftMeta: { source: source || null },
        createdAt: new Date(),
      });
      return { urlId: slug };
    },

    async persistDraft({ urlId, meta }) {
      const patch = {
        draftMeta: {
          source: meta.source || null,
          figmaMode: meta.figmaMode || null,
          tasks: Array.isArray(meta.tasks) ? meta.tasks : [],
        },
        updatedAt: new Date(),
      };
      if (meta.name) patch.title = meta.name;
      await urlRef(urlId).set(patch, { merge: true });
    },

    // deploySite (run by CreateFlow) already set type:'html' + activeVersion and
    // cleared draft via its makeLive path; this just lands the title.
    // `creationSource` (passed by CreateFlow, which knows the chosen mode) is
    // stamped as part of this existing merge write — html_drop for a plain drop
    // or a plugin export, ai_convert for the convert-then-drop path. The COUNTER
    // is fire-and-forget and never blocks this write.
    async finishDrop({ urlId, name, creationSource }) {
      const patch = { draft: del, updatedAt: new Date() };
      if (name && name.trim()) patch.title = name.trim();
      if (creationSource) patch.creationSource = creationSource;
      await urlRef(urlId).set(patch, { merge: true });
      if (creationSource) recordCreationSource(creationSource, 'aero');
      return { landing: `/sites/${urlId}` };
    },

    async finishFigmaEmbed({ urlId, link, name }) {
      // The draft was born HTML with a readable word-slug (createDraft). Figma
      // docs must serve at aeroapp.ai/{slug} with a RANDOM slug, so mint a fresh
      // unique random slug and overwrite generatedUrl as we flip type to figma.
      // The doc id stays as-is (the readable draft id) — only the generatedUrl
      // field, which the serving layer queries, changes.
      const slug = await generateUniqueSlug(db);
      const viewports = getDefaultViewports().map((v) => ({ ...v, figmaUrl: editFigmaUrl(link) }));
      const patch = {
        userId: uid,
        type: 'figma',
        aeroSite: true,
        isDraft: 'false',
        creationSource: 'figma_embed',
        generatedUrl: slug,
        viewports,
        urls: viewportsToLegacyFormat(viewports),
        draft: del,
        updatedAt: new Date(),
      };
      if (name && name.trim()) patch.title = name.trim();
      await urlRef(urlId).set(patch, { merge: true });
      recordCreationSource('figma_embed', 'aero');
      // The first site to go live starts the free plan's 7 day clock.
      await startTrialClock(db, uid, urlId);
      return { landing: `/sites/${urlId}`, generatedUrl: slug };
    },

    // Agent path: watch the draft doc; the agent's id-matched deploy_site
    // sets activeVersion server-side, so the moment it appears we clear the
    // draft (going live) and hand CreateFlow the landing.
    watchLive({ urlId }, onLive) {
      let done = false;
      return urlRef(urlId).onSnapshot((snap) => {
        const d = snap.data();
        if (!done && d && d.activeVersion) {
          done = true;
          urlRef(urlId).set({ draft: del, updatedAt: new Date() }, { merge: true });
          onLive(`/sites/${urlId}`);
        }
      });
    },

    async discard({ urlId }) {
      await discardDraft({ uid, urlId });
    },
  };
}
